import { Phone, Clock, Shield, CheckCircle2, Wrench, Settings, DoorOpen, Zap, FileCheck, Award, MapPin, Star, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useState } from "react";
import { toast } from "sonner";
import heroImage from "@/assets/hero-garage.jpg";
import emergencyRepairImage from "@/assets/emergency-repair.jpg";
import springReplacementImage from "@/assets/spring-replacement.jpg";
import openerInstallImage from "@/assets/opener-installation.jpg";
import newDoorImage from "@/assets/new-door-install.jpg";
import cableRepairImage from "@/assets/cable-repair.jpg";
import maintenanceImage from "@/assets/maintenance-service.jpg";
import teamImage from "@/assets/about-team.jpg";
import logo from "@/assets/abc-logo.jpg";
import testimonialMark from "@/assets/testimonial-mark.jpg";
import testimonialJessica from "@/assets/testimonial-jessica.jpg";
import testimonialLuis from "@/assets/testimonial-luis.jpg";
import testimonialSarah from "@/assets/testimonial-sarah.jpg";
import testimonialDavid from "@/assets/testimonial-david.jpg";

const Home = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    message: "",
    preferredTime: "",
  });

  const phoneNumber = "(386) 506-1956";
  const phoneLink = "tel:+13865061956";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Thank you! We'll contact you shortly to schedule your free quote.");
    setFormData({
      name: "",
      phone: "",
      email: "",
      address: "",
      message: "",
      preferredTime: "",
    });
  };

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen">
      {/* Header with Logo */}
      <header className="absolute top-0 left-0 right-0 z-20 py-3 md:py-4">
        <div className="container px-4">
          <div className="flex items-center justify-between">
            <img 
              src={logo} 
              alt="ABC Garage Door Pros" 
              className="h-16 md:h-24 w-auto bg-white/95 rounded-lg px-2 py-1 md:px-3 md:py-2 shadow-lg"
              width="120"
              height="80"
            />
            <Button size="lg" variant="secondary" className="hidden sm:flex" asChild>
              <a href={phoneLink}>
                <Phone className="mr-2 h-5 w-5" />
                Call Now for Same-Day Service
              </a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/40" />
        </div>
        
        <div className="container relative z-10 px-4 pt-32 pb-20 md:pt-40 lg:pt-48 lg:pb-32">
          <div className="max-w-3xl text-white">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Same-Day Garage Door Repair in Orlando, FL
            </h1>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Fast, professional garage door repair, installation, and opener service—available 7 days a week across Orlando and nearby areas.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button size="lg" className="text-lg px-8 py-6" asChild>
                <a href={phoneLink}>
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now for Same-Day Service
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-6"
                onClick={scrollToContact}
              >
                Request a Free Quote
              </Button>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <span className="font-medium">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Award className="h-5 w-5 text-accent" />
                <span className="font-medium">Family-Owned Local Business</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Shield className="h-5 w-5 text-accent" />
                <span className="font-medium">Honest Upfront Pricing</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Your Garage Door Matters */}
      <section className="py-16 bg-secondary">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Why Your Garage Door Matters</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Your garage door is one of the most-used entry points to your home. A properly functioning garage door ensures your family's safety, protects your vehicles and belongings from weather and theft, and adds significant value to your property. Whether it's a broken spring, a malfunctioning opener, or wear and tear, addressing garage door issues quickly prevents costly repairs down the road and keeps your home secure.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Complete garage door solutions for residential and light commercial properties
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="h-48 mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={emergencyRepairImage} 
                    alt="Emergency garage door repair service in Orlando - available 24/7" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <Zap className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Emergency Garage Door Repair</h3>
                    <p className="text-muted-foreground">
                      24/7 emergency service for urgent repairs. We fix broken springs, cables, panels, and more on the spot.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-48 mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={springReplacementImage} 
                    alt="Garage door spring replacement and repair service" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <Settings className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Broken Spring Replacement</h3>
                    <p className="text-muted-foreground">
                      Fast replacement of broken torsion and extension springs with high-quality parts backed by warranty.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-48 mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={openerInstallImage} 
                    alt="Professional garage door opener installation and repair" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <DoorOpen className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Garage Door Opener Repair & Installation</h3>
                    <p className="text-muted-foreground">
                      Expert service for all major brands. We repair or install smart openers with Wi-Fi connectivity.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-48 mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={newDoorImage} 
                    alt="New garage door installation service in Orlando" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <DoorOpen className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">New Garage Door Installation</h3>
                    <p className="text-muted-foreground">
                      Beautiful, energy-efficient garage doors in modern styles. Free estimates and professional installation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-48 mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={cableRepairImage} 
                    alt="Garage door cable repair and off-track door service" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <Wrench className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Off-Track Door & Cable Repair</h3>
                    <p className="text-muted-foreground">
                      We safely realign off-track doors and replace frayed or broken cables to restore smooth operation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-48 mb-4 rounded-lg overflow-hidden">
                  <img 
                    src={maintenanceImage} 
                    alt="Regular garage door maintenance and tune-up service" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <FileCheck className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Maintenance & Safety Tune-Ups</h3>
                    <p className="text-muted-foreground">
                      Preventive maintenance to extend door life. Includes lubrication, balance check, and safety tests.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-secondary">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={teamImage} 
                alt="ABC Garage Door Pros professional team in Orlando" 
                className="rounded-lg shadow-xl w-full"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">About ABC Garage Door Pros</h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                We're a local, family-owned team with years of experience serving the Orlando community. We take pride in delivering fast, reliable service with a personal touch. Whether you need an emergency repair or a brand-new door, we're fully licensed and insured to handle it all.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">Same-Day & Next-Day Appointments</h3>
                    <p className="text-muted-foreground">We understand urgency—most repairs are completed within hours of your call.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">Transparent Prices Before We Start</h3>
                    <p className="text-muted-foreground">No hidden fees—you'll know exactly what you're paying before any work begins.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg">Friendly Technicians, Clean Work</h3>
                    <p className="text-muted-foreground">Our team is professional, courteous, and always leaves your property clean and tidy.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              What makes ABC Garage Door Pros the trusted choice for homeowners across Central Florida
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Fast Response in Orlando</h3>
              <p className="text-muted-foreground">
                We arrive quickly and fix most issues in one visit, getting your life back to normal.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Expert Technicians</h3>
              <p className="text-muted-foreground">
                Trained on all major garage door brands and openers—no job is too complex for our team.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Honest Pricing</h3>
              <p className="text-muted-foreground">
                Clear quotes up front with no surprise fees—you'll always know what you're paying.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Safety First</h3>
              <p className="text-muted-foreground">
                We repair and test everything to ensure your door runs smoothly and safely for years to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-secondary">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto text-center">
            <MapPin className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Serving Orlando & Nearby Areas</h2>
            <p className="text-lg text-muted-foreground mb-8">
              We proudly serve Orlando, Winter Park, Kissimmee, Altamonte Springs, Sanford, and nearby neighborhoods throughout Central Florida. Wherever you are in the greater Orlando area, we're ready to help with all your garage door needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {["Orlando", "Winter Park", "Kissimmee", "Altamonte Springs", "Sanford", "Lake Mary", "Maitland", "Oviedo"].map((city) => (
                <span key={city} className="px-4 py-2 bg-background rounded-full text-sm font-medium">
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-20">
        <div className="container px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">What Our Customers Say</h2>
            <p className="text-lg text-muted-foreground">
              Real feedback from satisfied homeowners across Orlando
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img 
                    src={testimonialMark} 
                    alt="Mark R. - satisfied customer" 
                    className="w-16 h-16 rounded-full object-cover"
                    loading="lazy"
                    width="64"
                    height="64"
                  />
                  <div className="flex-1">
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <p className="font-semibold mb-1">Mark R.</p>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  "They arrived within 2 hours and had my broken spring replaced in 30 minutes. Fair pricing and super professional. Highly recommend!"
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img 
                    src={testimonialJessica} 
                    alt="Jessica P. - satisfied customer" 
                    className="w-16 h-16 rounded-full object-cover"
                    loading="lazy"
                    width="64"
                    height="64"
                  />
                  <div className="flex-1">
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <p className="font-semibold mb-1">Jessica P.</p>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  "My garage door went off-track and I was panicking. The technician was friendly, explained everything, and fixed it quickly. Great service!"
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img 
                    src={testimonialLuis} 
                    alt="Luis G. - satisfied customer" 
                    className="w-16 h-16 rounded-full object-cover"
                    loading="lazy"
                    width="64"
                    height="64"
                  />
                  <div className="flex-1">
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <p className="font-semibold mb-1">Luis G.</p>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  "Needed a new opener installed and they did an amazing job. Very clean work and the price was better than other quotes I got."
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img 
                    src={testimonialSarah} 
                    alt="Sarah M. - satisfied customer" 
                    className="w-16 h-16 rounded-full object-cover"
                    loading="lazy"
                    width="64"
                    height="64"
                  />
                  <div className="flex-1">
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <p className="font-semibold mb-1">Sarah M.</p>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  "Same-day service was a lifesaver! My door wouldn't close and they came out right away. Fixed it fast and the price was reasonable."
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img 
                    src={testimonialDavid} 
                    alt="David K. - satisfied customer" 
                    className="w-16 h-16 rounded-full object-cover"
                    loading="lazy"
                    width="64"
                    height="64"
                  />
                  <div className="flex-1">
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <p className="font-semibold mb-1">David K.</p>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  "Very honest company. They could have sold me a new door, but instead repaired what I had for much less. Will definitely use them again!"
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-secondary">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-left">Do you offer same-day service in Orlando?</AccordionTrigger>
                <AccordionContent>
                  Yes! We offer same-day and next-day appointments for most repair requests. Emergency service is available 24/7 when you need urgent help with your garage door.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="text-left">What areas do you cover?</AccordionTrigger>
                <AccordionContent>
                  We serve Orlando and all nearby areas including Winter Park, Kissimmee, Altamonte Springs, Sanford, Lake Mary, Maitland, Oviedo, and surrounding neighborhoods in Central Florida.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger className="text-left">How much does garage door repair usually cost?</AccordionTrigger>
                <AccordionContent>
                  Costs vary depending on the issue. Spring replacements typically range from $150-$300, while opener repairs can be $100-$250. We always provide a clear, upfront quote before starting any work—no hidden fees.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger className="text-left">Do you repair all garage door opener brands?</AccordionTrigger>
                <AccordionContent>
                  Absolutely! Our technicians are trained to repair and install all major garage door opener brands including LiftMaster, Chamberlain, Genie, Craftsman, and more.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger className="text-left">Can you come on weekends or evenings?</AccordionTrigger>
                <AccordionContent>
                  Yes, we're available 7 days a week including weekends and offer flexible scheduling. We understand garage door problems don't wait for business hours, so we're here when you need us.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger className="text-left">Do you offer warranties on your work?</AccordionTrigger>
                <AccordionContent>
                  Yes! All our repairs come with a warranty on both parts and labor. We use high-quality components and stand behind our work to ensure your complete satisfaction.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20">
        <div className="container px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Need Garage Door Help? Contact Us.</h2>
              <p className="text-lg text-muted-foreground">
                Fill out the form below and we'll call you back quickly to provide a free estimate. Or call us directly at{" "}
                <a href={phoneLink} className="text-primary font-semibold hover:underline">
                  {phoneNumber}
                </a>
              </p>
            </div>

            <Card>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Address (optional)</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main St, Orlando, FL"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Short description of the issue *</Label>
                    <Textarea
                      id="message"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us what's happening with your garage door..."
                      rows={4}
                    />
                  </div>

                  <div>
                    <Label htmlFor="preferredTime">Preferred time to contact</Label>
                    <Input
                      id="preferredTime"
                      value={formData.preferredTime}
                      onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                      placeholder="e.g., Morning, Afternoon, Evening"
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full">
                    <Mail className="mr-2 h-5 w-5" />
                    Request Free Quote
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-white py-12">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src={logo} 
                alt="ABC Garage Door Pros" 
                className="h-24 w-auto bg-white rounded-lg px-3 py-2 mb-4"
              />
              <p className="text-white/80 mb-4">
                Your trusted local garage door experts serving Orlando and surrounding areas.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-white/80 hover:text-white transition-colors">
                    Home
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-white/80 hover:text-white transition-colors">
                    Services
                  </a>
                </li>
                <li>
                  <a href="#about" className="text-white/80 hover:text-white transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-white/80 hover:text-white transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href={phoneLink} className="hover:text-white transition-colors">
                    {phoneNumber}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:info@abcgaragedoorpros.com" className="hover:text-white transition-colors">
                    info@abcgaragedoorpros.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>Orlando, FL</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Service Hours</h4>
              <p className="text-white/80">
                Monday - Sunday<br />
                7:00 AM - 9:00 PM<br />
                <span className="text-accent font-semibold">Emergency Service 24/7</span>
              </p>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 text-center text-white/60">
            <p>
              Serving Orlando and surrounding areas. © {new Date().getFullYear()} ABC Garage Door Pros. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;